import React, { useState, useEffect, useCallback } from 'react'
import { Link, useForm, router, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Divider,
  Snackbar,
  CircularProgress,
  Skeleton,
} from '@mui/material'
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Upload as UploadIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { PageHeader, FlexRow, FlexBetween, CodeBlock } from '@/components/styled'
import type { Backend, Account, FileItem, Breadcrumb } from '@/types'

interface Props {
  backend: Backend
  prefix: string
  breadcrumbs: Breadcrumb[]
  virtualBucketPaths: string[]
}

interface ListingResponse {
  folders: string[]
  files: FileItem[]
  isTruncated: boolean
  continuationToken?: string
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export default function FileBrowserShow({
  backend,
  prefix,
  breadcrumbs,
  virtualBucketPaths,
}: Props) {
  const { currentAccount } = usePage<{ currentAccount: Account }>().props
  const [folderDialogOpen, setFolderDialogOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedItem, setSelectedItem] = useState<{ key: string; isFolder: boolean } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [uploadMakePublic, setUploadMakePublic] = useState(false)
  const [aclLoading, setAclLoading] = useState(false)
  const [publicUrlDialogOpen, setPublicUrlDialogOpen] = useState(false)
  const [selectedPublicUrl, setSelectedPublicUrl] = useState('')
  const [snackbarMessage, setSnackbarMessage] = useState('')

  // Async listing state
  const [folders, setFolders] = useState<string[]>([])
  const [files, setFiles] = useState<FileItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [continuationToken, setContinuationToken] = useState<string | undefined>()
  const [isTruncated, setIsTruncated] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Download URL state (lazy loading)
  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({})
  const [downloadUrlLoading, setDownloadUrlLoading] = useState<Record<string, boolean>>({})

  const folderForm = useForm({ folder_name: '' })

  const basePath = `/accounts/${currentAccount.slug}/backends/${backend.slug}/browser`

  const fetchListing = useCallback(async (token?: string) => {
    const isLoadMore = !!token
    if (isLoadMore) {
      setLoadingMore(true)
    } else {
      setLoading(true)
      setLoadError(null)
    }

    try {
      const params = new URLSearchParams()
      if (prefix) params.set('prefix', prefix)
      if (token) params.set('continuation_token', token)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      const response = await fetch(`${basePath}/list_contents?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
      })

      if (!response.ok) throw new Error('Failed to load listing')

      const data: ListingResponse = await response.json()

      if (isLoadMore) {
        setFolders(prev => [...prev, ...data.folders])
        setFiles(prev => [...prev, ...data.files])
      } else {
        setFolders(data.folders)
        setFiles(data.files)
      }

      setIsTruncated(data.isTruncated)
      setContinuationToken(data.continuationToken || undefined)
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Failed to load listing')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [basePath, prefix])

  useEffect(() => {
    fetchListing()
  }, [fetchListing])

  const handleLoadMore = () => {
    if (continuationToken) {
      fetchListing(continuationToken)
    }
  }

  const fetchDownloadUrl = async (key: string) => {
    if (downloadUrls[key] || downloadUrlLoading[key]) return downloadUrls[key]

    setDownloadUrlLoading(prev => ({ ...prev, [key]: true }))

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      const response = await fetch(
        `${basePath}/presign_download?key=${encodeURIComponent(key)}&prefix=${encodeURIComponent(prefix)}`,
        {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
        }
      )

      if (!response.ok) throw new Error('Failed to get download URL')

      const data = await response.json()
      setDownloadUrls(prev => ({ ...prev, [key]: data.url }))
      return data.url
    } catch (error) {
      console.error('Failed to get download URL:', error)
      return null
    } finally {
      setDownloadUrlLoading(prev => ({ ...prev, [key]: false }))
    }
  }

  const handleDownload = async (key: string) => {
    const url = downloadUrls[key] || await fetchDownloadUrl(key)
    if (url) window.open(url, '_blank')
  }

  const handleCreateFolder = (e: React.FormEvent) => {
    e.preventDefault()
    folderForm.post(`${basePath}/create_folder?prefix=${encodeURIComponent(prefix)}`, {
      onSuccess: () => {
        setFolderDialogOpen(false)
        folderForm.reset()
        fetchListing()
      },
    })
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = e.target.files
    if (!uploadedFiles?.length) return

    setUploading(true)
    setUploadProgress(0)

    try {
      const totalFiles = uploadedFiles.length
      let completedFiles = 0
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      for (const file of Array.from(uploadedFiles)) {
        const formData = new FormData()
        formData.append('files[]', file)
        if (uploadMakePublic) {
          formData.append('acl', 'public-read')
        }

        const response = await fetch(`${basePath}/upload?prefix=${encodeURIComponent(prefix)}`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken || '',
          },
          body: formData,
        })

        if (!response.ok) {
          throw new Error('Upload failed')
        }

        completedFiles++
        setUploadProgress((completedFiles / totalFiles) * 100)
      }

      fetchListing()
    } catch (error) {
      console.error('Upload failed:', error)
    } finally {
      setUploading(false)
      setUploadProgress(0)
      setUploadMakePublic(false)
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, key: string, isFolder: boolean) => {
    setMenuAnchorEl(event.currentTarget)
    setSelectedItem({ key, isFolder })
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setSelectedItem(null)
  }

  const handleDelete = () => {
    if (!selectedItem) return
    router.delete(`${basePath}?key=${encodeURIComponent(selectedItem.key)}&prefix=${encodeURIComponent(prefix)}`, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        handleMenuClose()
        fetchListing()
      },
    })
  }

  const handleToggleAcl = async (file: FileItem) => {
    setAclLoading(true)
    const newAcl = file.isPublic ? 'private' : 'public-read'

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      const response = await fetch(`${basePath}/set_acl?prefix=${encodeURIComponent(prefix)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({ key: file.key, acl: newAcl }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update ACL')
      }

      fetchListing()
    } catch (error) {
      console.error('ACL update failed:', error)
      setSnackbarMessage(error instanceof Error ? error.message : 'Failed to update ACL')
    } finally {
      setAclLoading(false)
      handleMenuClose()
    }
  }

  const handleCopyPublicUrl = (url: string) => {
    const fallbackCopy = () => {
      try {
        const textarea = document.createElement('textarea')
        textarea.value = url
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        setSnackbarMessage('Public URL copied to clipboard')
      } catch {
        setSelectedPublicUrl(url)
        setPublicUrlDialogOpen(true)
      }
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setSnackbarMessage('Public URL copied to clipboard')
      }).catch(fallbackCopy)
    } else {
      fallbackCopy()
    }
  }

  const parentPrefix = breadcrumbs.length > 1
    ? breadcrumbs[breadcrumbs.length - 2].path
    : ''

  return (
    <ManagementLayout>
      {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />}

      <PageHeader
        title="Browse Files"
        breadcrumb={
          <Breadcrumbs>
            <Link href={`/accounts/${currentAccount.slug}/backends`}>Backends</Link>
            <Link href={`/accounts/${currentAccount.slug}/backends/${backend.slug}`}>{backend.name}</Link>
            <Typography color="text.primary">Browse</Typography>
          </Breadcrumbs>
        }
      />

      <Box sx={{ mb: 2 }}>
        <CodeBlock>s3://{backend.bucket}/</CodeBlock>
      </Box>

      <FlexBetween sx={{ mb: 2 }}>
        <Breadcrumbs>
          <Link href={basePath}>Root</Link>
          {breadcrumbs.map((crumb, idx) => (
            <Link key={idx} href={`${basePath}?prefix=${encodeURIComponent(crumb.path)}`}>
              {crumb.name}
            </Link>
          ))}
        </Breadcrumbs>
        <FlexRow sx={{ gap: 1, alignItems: 'center' }}>
          <Button startIcon={<CreateNewFolderIcon />} onClick={() => setFolderDialogOpen(true)}>
            New Folder
          </Button>
          <Button variant="contained" component="label" startIcon={<UploadIcon />} disabled={uploading}>
            Upload
            <input type="file" hidden multiple onChange={handleFileUpload} />
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={uploadMakePublic}
                onChange={(e) => setUploadMakePublic(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Make public</Typography>}
          />
        </FlexRow>
      </FlexBetween>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Modified</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prefix && (
              <TableRow hover>
                <TableCell colSpan={4}>
                  <Link href={`${basePath}?prefix=${encodeURIComponent(parentPrefix)}`}>
                    <FlexRow sx={{ gap: 1 }}>
                      <ArrowBackIcon fontSize="small" /> ..
                    </FlexRow>
                  </Link>
                </TableCell>
              </TableRow>
            )}

            {loading ? (
              <>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell><Skeleton variant="text" width={200} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell align="right"><Skeleton variant="circular" width={24} height={24} /></TableCell>
                  </TableRow>
                ))}
              </>
            ) : loadError ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    {loadError}
                  </Typography>
                  <Button variant="outlined" size="small" onClick={() => fetchListing()}>
                    Retry
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {folders.map((folderPath) => {
                  const folderName = folderPath.replace(/\/$/, '').split('/').pop() || folderPath
                  const isVirtualBucket = virtualBucketPaths.includes(folderPath)
                  return (
                    <TableRow key={folderPath} hover>
                      <TableCell>
                        <Link href={`${basePath}?prefix=${encodeURIComponent(folderPath)}`}>
                          <FlexRow sx={{ gap: 1 }}>
                            <FolderIcon sx={{ color: 'warning.main' }} />
                            {folderName}
                            {isVirtualBucket && (
                              <Chip label="Virtual Bucket" size="small" color="primary" sx={{ ml: 1 }} />
                            )}
                          </FlexRow>
                        </Link>
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, folderPath, true)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}

                {files.map((file) => (
                  <TableRow key={file.key} hover>
                    <TableCell>
                      <FlexRow sx={{ gap: 1 }}>
                        <FileIcon sx={{ color: 'grey.500' }} />
                        {file.name}
                        {file.isPublic && (
                          <Chip label="Public" size="small" color="success" sx={{ ml: 0.5 }} />
                        )}
                      </FlexRow>
                    </TableCell>
                    <TableCell>{formatFileSize(file.size)}</TableCell>
                    <TableCell>{new Date(file.last_modified).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      {file.isPublic && file.publicUrl && (
                        <Tooltip title="Copy public URL">
                          <IconButton size="small" onClick={() => handleCopyPublicUrl(file.publicUrl!)}>
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(file.key)}
                        disabled={downloadUrlLoading[file.key]}
                      >
                        {downloadUrlLoading[file.key] ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon />
                        )}
                      </IconButton>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, file.key, false)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {folders.length === 0 && files.length === 0 && !prefix && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        This bucket is empty. Upload files or create folders to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isTruncated && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} /> : undefined}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        {selectedItem && !selectedItem.isFolder && (() => {
          const file = files.find((f) => f.key === selectedItem.key)
          return (
            <>
              <MenuItem
                onClick={() => {
                  if (file) handleDownload(file.key)
                  handleMenuClose()
                }}
              >
                Download
              </MenuItem>
              {file?.canManageAcl ? (
                <MenuItem
                  onClick={() => file && handleToggleAcl(file)}
                  disabled={aclLoading}
                >
                  {file?.isPublic ? (
                    <><LockIcon fontSize="small" sx={{ mr: 1 }} /> Make Private</>
                  ) : (
                    <><PublicIcon fontSize="small" sx={{ mr: 1 }} /> Make Public</>
                  )}
                </MenuItem>
              ) : (
                <Tooltip title="File is not under any virtual bucket">
                  <span>
                    <MenuItem disabled>Make Public</MenuItem>
                  </span>
                </Tooltip>
              )}
              {file?.isPublic && file.publicUrl && (
                <MenuItem
                  onClick={() => {
                    handleCopyPublicUrl(file.publicUrl!)
                    handleMenuClose()
                  }}
                >
                  <LinkIcon fontSize="small" sx={{ mr: 1 }} /> Copy Public URL
                </MenuItem>
              )}
              <Divider />
            </>
          )
        })()}
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true)
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog open={folderDialogOpen} onClose={() => setFolderDialogOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleCreateFolder}>
          <DialogTitle>Create New Folder</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Folder Name"
              fullWidth
              margin="normal"
              value={folderForm.data.folder_name}
              onChange={(e) => folderForm.setData('folder_name', e.target.value)}
              placeholder="my-folder"
              error={!!folderForm.errors.folder_name}
              helperText={folderForm.errors.folder_name}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFolderDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={folderForm.processing}>
              Create Folder
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title={selectedItem?.isFolder ? 'Delete Folder' : 'Delete File'}
        message={
          selectedItem?.isFolder
            ? 'Are you sure you want to delete this folder and all its contents? This action cannot be undone.'
            : 'Are you sure you want to delete this file? This action cannot be undone.'
        }
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false)
          handleMenuClose()
        }}
      />

      {/* Public URL Dialog (fallback when clipboard fails) */}
      <Dialog open={publicUrlDialogOpen} onClose={() => setPublicUrlDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Public URL</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={selectedPublicUrl}
            slotProps={{ input: { readOnly: true } }}
            margin="normal"
            onFocus={(e) => e.target.select()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublicUrlDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </ManagementLayout>
  )
}
